/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import {
  COLUMNTYPE_NUMBER,
  COLUMNTYPE_TIMESTAMP,
  COLUMNTYPE_LITERAL,
  COLUMNTYPE_JSON,
  COLUMNTYPE_ARRAY,
  SCOPE_FILTER_TYPES,
  DATA_ITEM_MATCH_FILTER_OPTIONS,
} from 'constants';

import { defineMessages } from 'react-intl';
import difference from 'lodash/difference';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import some from 'lodash/some';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import filter from 'lodash/filter';
import isString from 'lodash/isString';

import {
  fieldRequired,
  validateUniqueName,
  validateSpecialCharacters,
  validateMaxLength,
  validateFirstChar,
} from 'formFunctions';
import { parseSchedule, parseBacktrack } from 'scheduleFormFunctions';
import { generateOutputMetricName } from 'dataFormatterFunctions';

const GRAIN_MAXLENGTH = 255;
const messages = defineMessages({
  oneKPI: 'You must enable one function.',
  whitespace: 'The entry cannot contain a space.',
  outputName: '{granularity}_{name}',
  inputOutputName: '{name}_{input}',
  placeholderLiteral: 'Enter string',
  placeHolderArray: 'Enter values',
  placeholderJSON: 'Enter JSON',
  placeholderNumber: 'Enter number',
  placeholderTimestamp: 'Enter timestamp',
  cardinalityExact: 'You need to specify exactly {cardinality} items.',
  cardinalityLeast: 'You need to specify at least {cardinality} items.',
  cardinalityMax: 'You need to specify less than {cardinality} items.',
  cardinalityBetween: 'You need to specify between {cardinalityMin} and  {cardinalityMax} items.',
});

export function parseKPI(kpi, granularity) {
  return omit(
    {
      ...kpi,
      output: { name: kpi.outputMetricName },
      granularity,
    },
    ['outputMetricName', 'inputMeta', 'outputMeta']
  );
}

export function validateKPIName(name, items, field, props) {
  // validate that we don't have a name collision or special characters
  return (
    validateUniqueName(name, items, field, props) ||
    validateSpecialCharacters(name, props) ||
    validateMaxLength(name, props) ||
    validateFirstChar(name, props)
  );
}

export function initializeCreateGrainForm(aggregates, dimensions, grain) {
  // Update and delete usecase
  if (grain) {
    let customFrequency;
    let dataItems;
    if (grain.dimensions) {
      customFrequency = difference(
        grain.dimensions,
        dimensions.map(dimension => dimension.name)
      ); // anything in dimensions that's not a dimension is a customFrequency.
      dataItems = difference(
        dimensions.map(dimension =>
          grain?.dimensions?.includes(dimension.name) // add the selected bits to the table dimensions
            ? { ...dimension, isSelected: true }
            : dimension
        ),
        customFrequency
      ); // do not consider the custom frequency
    } else {
      dataItems = dimensions;
    }
    return merge({
      ...grain,
      id: grain.name, // save the original name in the ID field for reposting
      hasDimensions: some(dataItems, 'isSelected'), // only check has dimensions if some were selected
      dimensions: dataItems || [],
      frequency:
        isNil(customFrequency) || customFrequency.length === 0
          ? grain.frequency
          : customFrequency[0],
    });
  }
  // Create usecase
  return {
    name: '',
    hasDimensions: some(dimensions, 'isSelected'),
    dimensions: dimensions
      ? dimensions.map(dimension => ({ ...dimension, isSelected: false }))
      : [],
    frequency: aggregates[0].name,
    aggregateByDevice: true,
  };
}

export function validateNoSpaces(value, props) {
  const {
    intl: { formatMessage },
  } = props;
  const foundSpaces = value.match(/\s+/);
  return foundSpaces && foundSpaces.length > 0 // check name for spaces
    ? formatMessage(messages.whitespace)
    : undefined;
}

export function validateCreateGrainForm(values, props) {
  const { granularities } = props;
  const errors = {};

  let nameFieldError =
    fieldRequired(values.name, values, props) ||
    validateNoSpaces(values.name, props) ||
    validateSpecialCharacters(values.name, props) ||
    validateMaxLength(values.name, props, GRAIN_MAXLENGTH);

  if (!nameFieldError && isNil(props.grain)) {
    // Only validate uniqueness if new
    nameFieldError = validateUniqueName(values.name, granularities, 'name', props);
  }

  if (nameFieldError) {
    errors.name = nameFieldError;
  }

  return errors;
}

export function hasCustomFrequency(availableAggregates, frequency) {
  return find(
    filter(availableAggregates, aggregate => aggregate.columnType), // custom frequencies have a column type
    { name: frequency }
  ); // determine whether the frequency is a default time frequency
}

export function parseCreateGrainForm(data, availableAggregates, selectedDimensions) {
  const isFrequencyCustom = hasCustomFrequency(availableAggregates, data.frequency);
  const dimensions = data.hasDimensions ? selectedDimensions : []; // selected Dimensions comes from table, not the form
  if (isFrequencyCustom) {
    // Custom frequencies go in the dataItems
    dimensions.push(data.frequency);
  }
  return omit(
    {
      ...data,
      description: data.name,
      dimensions,
    },
    ['hasDimensions', 'dataItems', isFrequencyCustom ? 'frequency' : undefined] // hasDimensions is UI only (not sent to backend)
  );
}

/**
 * If the new granularity changes, forcibly update the output metric names for non running kpis
 *
 * @param {*} data existing Form Data
 * @param {*} granularity, new Granularity
 * @param {*} dataItem
 * @param {*} props
 */
export function reinitializeOutputMetricNames(data, granularity, dataItem, props) {
  return merge({}, data, {
    kpiFunctions: data.kpiFunctions.map(kpi =>
      generateOutputMetricName(granularity, kpi, dataItem, props)
    ),
    granularity,
  });
}

export function validateCardinality(value, jsonSchema, required, formatMessage) {
  const arrayValue = (Array.isArray(value) ? value : [value]).filter(item =>
    typeof item === 'string' ? !isEmpty(item) : !isNil(item)
  ); // convert literal values to array
  // Validate if required or if they've entered values
  if (
    (required || (arrayValue && arrayValue.length > 0)) &&
    (jsonSchema?.minItems || jsonSchema?.maxItems)
  ) {
    const { minItems, maxItems } = jsonSchema;
    if (minItems === maxItems) {
      // make sure MS field  has exactly (N) amount of options picked.
      if (arrayValue.length !== minItems) {
        return formatMessage(messages.cardinalityExact, {
          cardinality: minItems,
        });
      }
    } else if (minItems && !maxItems) {
      // check if MS has at least as much as (N) numbers of options picked.
      if (arrayValue.length < minItems) {
        return formatMessage(messages.cardinalityLeast, {
          cardinality: minItems,
        });
      }
    } else if (maxItems && !minItems) {
      // check if MS has at least as much as (N) numbers of options picked.
      if (arrayValue.length > maxItems) {
        return formatMessage(messages.cardinalityMax, {
          cardinality: maxItems,
        });
      }
    }
    // check if MS has between (N) and (X) amount
    else if (arrayValue.length < minItems || arrayValue.length > maxItems) {
      return formatMessage(messages.cardinalityBetween, {
        cardinalityMin: minItems,
        cardinalityMax: maxItems,
      });
    }
  }
  return undefined;
}

/**
 * This functions job is to examine the outputMetadata, inputMetadata, and SOMETIMES has to look at the current input form setting to figure out what kind of dataType an Output should be
 * @param {*} outputMetadata
 * @param {*} index
 * @param {*} inputMetadata
 * @param {*} dataItems
 * @param {string} input the current value of this input dataItem
 * @returns
 */
export const determineDataTypeForOutput = (
  outputMetadata,
  index,
  inputMetadata,
  dataItems,
  input
) => {
  if (outputMetadata) {
    if (outputMetadata.dataType) {
      return outputMetadata.dataType;
    }
    // if the dataTypeForArray is set in the Array use it
    if (outputMetadata.dataTypeForArray) {
      return outputMetadata.dataTypeForArray[
        index < outputMetadata.dataTypeForArray.length ? index : 0
      ];
    }
    if (outputMetadata.dataTypeFrom) {
      // if the dataType is supposed to come from the inputMetadata item, then use it
      const matchingInputMetadataItem = inputMetadata.find(
        inputMetaDatum => inputMetaDatum.name === outputMetadata.dataTypeFrom
      );
      // if the input data item has an explicit type then use it (as long as it is not ARRAY)
      if (
        matchingInputMetadataItem?.dataType &&
        matchingInputMetadataItem?.dataType !== COLUMNTYPE_ARRAY
      ) {
        return matchingInputMetadataItem.dataType;
      }
      // if the input data item has an ARRAY dataType, we need to check the dataTypeForArray
      if (
        matchingInputMetadataItem?.dataType &&
        matchingInputMetadataItem?.dataType === COLUMNTYPE_ARRAY &&
        matchingInputMetadataItem?.dataTypeForArray
      ) {
        return matchingInputMetadataItem.dataTypeForArray[
          index < matchingInputMetadataItem.dataTypeForArray.length ? index : 0
        ];
      }
      // otherwise, this is an input where a dataItem was selected, get the data item name, then return it's type
      return dataItems.find(dataItem => dataItem.name === input)?.columnType;
    }
  }
  return null;
};
/**
 * This function generates a output record for each multi-select input selection when outputs are dependent on the inputs.
 * This function is called once for each multi-select input selection that the user has made.
 *
 * @param {} catalogMetadata outputMeta for this output field from the function catalog, needed to store transient, and type options {name: 'outputName',transient:true,dataType: 'string}
 * @param {*} output the current output object that stores the names of these output field in an array, if populated will look like {'output1':['output1_inputselection1','output1_inputselection2']}
 * @param {*} outputMeta, the INSTANCE-LEVEL metadata for this output, might be empty if all the metadata lives at the catalog level, and didn't need user-input
 * @param {*} input, the name of the input field that this output is dependent on
 * @param {*} index, the index of the current output in the output names array
 * @param {*} formatMessage
 */
export const generateDependentOutput = (
  catalogOutputMetadata,
  output,
  outputMeta,
  input,
  index,
  formatMessage,
  catalogInputMetadata,
  dataItems
) => {
  const outputInstanceName = output[catalogOutputMetadata.name] // If the user has previously created this output it would be here
    ? Array.isArray(output[catalogOutputMetadata.name])
      ? output[catalogOutputMetadata.name][index]
      : isString(output[catalogOutputMetadata.name])
      ? output[catalogOutputMetadata.name]
      : undefined
    : undefined; // The user has never set this output name before

  const determinedDataType = determineDataTypeForOutput(
    catalogOutputMetadata,
    index,
    catalogInputMetadata,
    dataItems,
    input
  );

  return {
    originalOutputMetricName: outputInstanceName, // If the name has been set previously it will be used
    ...output,
    name: formatMessage(messages.inputOutputName, {
      // otherwise use the generated output name that also contains the input name
      input,
      name: catalogOutputMetadata.name,
    }),
    dataType: determinedDataType,
    ...(outputMeta
      ? {
          ...outputMeta[outputInstanceName],
        }
      : {}), // bring in any instance level metadata
    // setup store in database no matter what
    storeInDatabase: !isNil(outputMeta?.[outputInstanceName]?.transient)
      ? !outputMeta?.[outputInstanceName]?.transient
      : !catalogOutputMetadata.transient,
    metadata: catalogOutputMetadata, // attach the function-level metadata
  };
};

/**
 * This functions JOB is to figure out what kind of dataType an output is, give the output a default name, generate ENOUGH outputs
 * if a particular output in linked to an input that will generate MULTIPLE outputs (cardinality)
 * @param {*} formValues
 * @param {*} catalogMeta
 * @param {*} output
 * @param {*} outputMeta
 * @param {*} formatMessage
 * @param {*} catalogInputMetadata
 * @param {*} dataItems
 * @returns
 */

export function determineKPIOutputs(
  formValues,
  catalogMeta,
  output,
  outputMeta,
  formatMessage,
  catalogInputMetadata,
  dataItems
) {
  return catalogMeta.reduce((allOutputs, outputMetadata) => {
    if (
      (outputMetadata.cardinalityFrom || outputMetadata.dataTypeFrom) &&
      formValues &&
      formValues.input
    ) {
      // Need to generate an output record from the inputs
      const inputSelections =
        formValues.input[outputMetadata.cardinalityFrom || outputMetadata.dataTypeFrom];
      if (inputSelections) {
        if (Array.isArray(inputSelections)) {
          // support both arrays and single values
          inputSelections.forEach((input, index) => {
            allOutputs.push(
              generateDependentOutput(
                outputMetadata,
                output,
                outputMeta,
                input,
                index,
                formatMessage,
                catalogInputMetadata,
                dataItems
              )
            );
          });
        } else {
          allOutputs.push(
            generateDependentOutput(
              outputMetadata,
              output,
              outputMeta,
              inputSelections,
              0,
              formatMessage,
              catalogInputMetadata,
              dataItems
            )
          );
        }
      }
      return allOutputs;
    }
    // I don't need to look at the inputMetadata to figure out the output Type
    allOutputs.push({
      originalOutputMetricName: output[outputMetadata.name],
      ...output,
      name: outputMetadata.name,
      ...(outputMeta ? outputMeta[output[outputMetadata.name]] : {}), // bring in any instance level metadata
      metadata: outputMetadata,
      storeInDatabase: !outputMeta?.[output[outputMetadata.name]]?.transient,
    });
    return allOutputs;
  }, []);
}

/**
 * If the new granularity changes, forcibly update the output metric names for non running kpis
 *
 * @param {*} data existing table
 * @param {*} granularity, new Granularity
 * @param {*} props
 */
export function initializeCalculateKPIOutput(formValues, data, granularity, props) {
  const {
    intl: { formatMessage },
  } = props;

  return {
    ...formValues,
    output: data.map(output => {
      const generatedMetricName = granularity
        ? formatMessage(messages.outputName, {
            granularity,
            name: output.name,
          })
        : output.name;
      return {
        outputMetricName: output.originalOutputMetricName || generatedMetricName,
        ...output,
      };
    }),
    granularity, // set granularity for the form
  };
}

/**
 * Parse the scope data into the correct format expected by the API
 *
 * @param {Object} data form data
 * @param {Object} data.input data that was input to the form by the user
 * @param {Object} data.input.scope scope data that was input to the form by the user
 * @param {Array} data.input.scope.dimensions array of dimensions objects input by the user: {name: 'manufacturer', value: ['rentech']}
 * @param {String} filterType, the type of filter being applied: CHILD_NODE, EXPRESSIONS, or null
 */
export function formatScopeForAPI(data, filterTypeProp) {
  const filterType = filterTypeProp || data.input?.scope?.type;
  const nodeFilterMatchType =
    data.input?.scope?.nodeFilterMatchType ?? DATA_ITEM_MATCH_FILTER_OPTIONS.ANY;
  const { scope } = data.input;
  if (filterType === SCOPE_FILTER_TYPES.CHILD_NODE) {
    // Remove any dimensions that don't have both name and value defined
    const filteredDimensions = scope.dimensions?.filter(
      dimension => dimension.name && dimension.value?.length
    );

    // re-map for the API
    const parsedDimensions = [];
    filteredDimensions?.forEach(dimension => {
      const dimensionIndex = parsedDimensions.findIndex(
        searchedDimension => searchedDimension.name === dimension.name
      );
      if (dimensionIndex > -1) {
        if (parsedDimensions[dimensionIndex].value.length) {
          parsedDimensions[dimensionIndex].value = parsedDimensions[dimensionIndex].value.concat(
            dimension.value
          );
        }
      } else {
        parsedDimensions.push({
          name: dimension.name,
          value: Array.isArray(dimension.value) ? dimension.value : [dimension.value], // Make sure the value sent to the API is always an array
        });
      }
    });

    // Temporary dimension filter data map for back end testing
    // TODO: skip this extra map and just pass 'parsedDimensions' to the dimensions property on the return
    const temporaryDimensionFilterData = parsedDimensions.map((dimension, i) =>
      i === parsedDimensions.length - 1
        ? { ...dimension, operator: 'IN', condition: null }
        : { ...dimension, operator: 'IN', condition: 'AND' }
    );

    return {
      ...omit(scope, 'expression'),
      dimensions: temporaryDimensionFilterData,
      type: filterType,
      nodeFilterMatchType,
    };
  }
  if (filterType === SCOPE_FILTER_TYPES.EXPRESSIONS) {
    return {
      ...omit(scope, ['dimensions', 'nodeFilterMatchType']),
      type: filterType,
    };
  }
  return null;
}

/**
 * Convert the scope data back to the format that is expected by the UI components
 *
 * @param {Object} data data item details data
 * @param {Object} data.scope scope data
 * @param {Array} data.scope.dimensions array of dimensions objects returned by the API: {name: 'manufacturer', value: ['rentech, other_manufacturer'], condition: 'AND', operation: 'IN'}
 * @returns the array of dimensions (if it exists in data.scope) with an array of dimensions in a format that is expected by the UI components: [{name: 'manufacturer', value: 'rentech'}, {name: 'manufacturer', value: 'other_manufacturer'}]
 */
export function formatScopeForUI(data) {
  if (data?.kpiFunctionDto?.scope?.type === SCOPE_FILTER_TYPES.CHILD_NODE) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { scope } = data?.kpiFunctionDto;
    const deparsedDimensions = [];
    scope.dimensions.forEach(dimension => {
      if (Array.isArray(dimension.value)) {
        dimension.value.forEach(value => {
          deparsedDimensions.push({ name: dimension.name, value });
        });
      } else {
        deparsedDimensions.push({ name: dimension.name, value: dimension.value });
      }
    });
    return {
      ...data,
      kpiFunctionDto: {
        ...data.kpiFunctionDto,
        scope: { ...scope, dimensions: deparsedDimensions },
      },
    };
  }
  return data;
}

/**
 * Parse the input data into the correct format expected by the API
 *
 * @param {Object} data form data
 * @param {String} filterType, the type of filter being applied: CHILD_NODE, EXPRESSIONS, or null
 */
export function parseCalculateKPI(data, filterType) {
  const output = data.output.reduce((result, out) => {
    if (out.metadata.cardinalityFrom) {
      // assume this is an array since we infer its type
      result[out.metadata.name] = result[out.metadata.name] || []; // eslint-disable-line no-param-reassign
      result[out.metadata.name].push(out.outputMetricName);
    } else {
      // otherwise its a direct result
      result[out.metadata.name] = out.outputMetricName; // eslint-disable-line no-param-reassign
    }
    return result;
  }, {});

  const outputMeta = data.output.reduce((outputMetaMap, meta) => {
    /* eslint-disable no-param-reassign */
    outputMetaMap[meta.outputMetricName] = {};
    if (!isNil(meta.dataType)) {
      outputMetaMap[meta.outputMetricName].dataType = meta.dataType;

      /* eslint-enable no-param-reassign */
    }
    // need to convert back from storeInDatabase to transient
    /* eslint-disable-next-line no-param-reassign */
    outputMetaMap[meta.outputMetricName].transient = !meta.storeInDatabase;
    return outputMetaMap;
  }, {});

  return {
    ...omitBy(data, (value, key) => key.includes('datePickerRedux')), // This is a special requirement to remove the extra fields added by the datePicker to the form
    input: omit(data.input, 'scope'), // omit scope from this level as we will move it out to be at the top level
    scope: formatScopeForAPI(data, filterType),
    output,
    ...(!isEmpty(outputMeta) ? { outputMeta } : {}),
    schedule: parseSchedule(data),
    backtrack: parseBacktrack(data),
  };
}

export function generateCommonInputProps(fieldMetaData) {
  return {
    key: `field-${fieldMetaData.name}`,
    name: fieldMetaData.name,
    label: fieldMetaData.name,
    tooltip: fieldMetaData.description,
    learnMore: fieldMetaData.learnMore,
    required: fieldMetaData.required,
  };
}

export function generatePlaceholder(fieldMetaData, formatMessage) {
  switch (fieldMetaData.dataType) {
    case COLUMNTYPE_ARRAY:
      return formatMessage(messages.placeHolderArray);
    case COLUMNTYPE_JSON:
      return formatMessage(messages.placeholderJSON);
    case COLUMNTYPE_TIMESTAMP:
      return formatMessage(messages.placeholderTimestamp);
    case COLUMNTYPE_NUMBER:
      return formatMessage(messages.placeholderNumber);
    case COLUMNTYPE_LITERAL:
    default:
      return formatMessage(messages.placeholderLiteral);
  }
}
